import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input, Select } from '@artosera/ui';
import {
  CANAUX_CONVERSATION,
  CATEGORIES_CONVERSATION,
  VUES_BOITE,
  formatDate,
} from '@artosera/shared';
import {
  useConversations,
  useReglagesBoite,
  type FiltresBoite,
  type VueBoite,
} from '../../api/boite-reception.js';
import { useEquipe } from '../../api/equipe.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { ReperesAssistant } from './AssistantConversation.js';
import { RelancesVue } from './RelancesVue.js';
import { ConversationVue } from './ConversationVue.js';
import { NavigationBoite } from './NavigationBoite.js';
import styles from './Boite.module.css';
export function BoitePage() {
  const { galerie } = useShellContext();
  if (!galerieAModule(galerie, 'boite-reception') || !galerieALaction(galerie, 'inbox:read'))
    return <NotFoundPage />;
  return <Boite />;
}
function Boite() {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [recherche, setRecherche] = useState('');
  const q = useDebounce(recherche);

  const assistantActif = galerieAModule(galerie, 'assistant-reponse');

  const relances = assistantActif && params.get('vue') === 'relances';

  const vueParam = params.get('vue');
  const vue: VueBoite = VUES_BOITE.find((v) => v === vueParam) ?? 'a_traiter';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const filtres: FiltresBoite = {
    vue,
    q,
    canal: params.get('canal') ?? '',
    categorie: params.get('categorie') ?? '',
    etiquette: params.get('etiquette') ?? '',
    page,
    ...(assistantActif && params.get('urgentes') === 'true' ? { urgentes: true } : {}),
  };
  const liste = useConversations(galerieSlug, filtres, !relances);
  const reglages = useReglagesBoite(galerieSlug);
  const equipe = useEquipe(galerieSlug, { enabled: galerieALaction(galerie, 'memberships:read') });
  function filtrer(cle: string, valeur: string) {
    const prochain = new URLSearchParams(params);
    if (valeur) prochain.set(cle, valeur);
    else prochain.delete(cle);
    if (cle !== 'page') prochain.delete('page');
    setParams(prochain);
  }
  return (
    <section className={styles.page}>
      <header className={styles.gestes}>
        <h1>{t('regie:boiteReception.titre')}</h1>
        <Link to={`/${galerieSlug}/reglages/boite-reception`}>
          {t('regie:boiteReception.reglages')}
        </Link>
      </header>
      {assistantActif && <NavigationBoite courante={relances ? 'relances' : 'boite'} />}
      {relances ? (
        <RelancesVue />
      ) : (
        <div className={`${styles.volets} ${id ? styles.selection : ''}`}>
          <div className={styles.liste}>
            <Field htmlFor="boite-vue" label={t('regie:boiteReception.vue')}>
              <Select
                id="boite-vue"
                value={vue}
                onValueChange={(v) => filtrer('vue', v)}
                options={VUES_BOITE.map((value) => ({
                  value,
                  label: t(`regie:boiteReception.vues.${value}`),
                }))}
              />
            </Field>
            <Field htmlFor="boite-recherche" label={t('regie:boiteReception.recherche')}>
              <Input
                id="boite-recherche"
                type="search"
                maxLength={200}
                value={recherche}
                onChange={(e) => {
                  setRecherche(e.target.value);
                  if (page > 1) filtrer('page', '1');
                }}
              />
            </Field>
            {assistantActif && (
              <Checkbox
                id="boite-urgentes"
                label={t('regie:assistantReponse.urgentes')}
                checked={params.get('urgentes') === 'true'}
                onCheckedChange={(v) => filtrer('urgentes', v === true ? 'true' : '')}
              />
            )}
            <div className={styles.filtres}>
              <Field htmlFor="boite-canal" label={t('regie:boiteReception.canal')}>
                <Select
                  id="boite-canal"
                  value={filtres.canal}
                  onValueChange={(v) => filtrer('canal', v)}
                  options={[
                    { value: '', label: t('regie:boiteReception.tous') },
                    ...CANAUX_CONVERSATION.map((value) => ({
                      value,
                      label: t(`regie:boiteReception.canaux.${value}`),
                    })),
                  ]}
                />
              </Field>
              <Field htmlFor="boite-categorie" label={t('regie:boiteReception.categorie')}>
                <Select
                  id="boite-categorie"
                  value={filtres.categorie}
                  onValueChange={(v) => filtrer('categorie', v)}
                  options={[
                    { value: '', label: t('regie:boiteReception.toutes') },
                    ...CATEGORIES_CONVERSATION.map((value) => ({
                      value,
                      label: t(`regie:boiteReception.categories.${value}`),
                    })),
                  ]}
                />
              </Field>
              <Field htmlFor="boite-etiquette" label={t('regie:boiteReception.etiquette')}>
                <Select
                  id="boite-etiquette"
                  value={filtres.etiquette}
                  onValueChange={(v) => filtrer('etiquette', v)}
                  options={[
                    { value: '', label: t('regie:boiteReception.toutes') },
                    ...(reglages.data?.reglages.etiquettes ?? []).map((value) => ({
                      value,
                      label: value,
                    })),
                  ]}
                />
              </Field>
            </div>
            {liste.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
            {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
            {liste.data?.conversations.length === 0 ? (
              <p>{t('regie:boiteReception.vide')}</p>
            ) : null}
            {liste.data?.conversations.map((c) => (
              <Link
                key={c.id}
                to={`/${galerieSlug}/boite-reception/${c.id}?${params}`}
                className={`${styles.ligne} ${c.nonLue ? styles.nonLue : ''}`}
                aria-current={id === c.id ? 'page' : undefined}
              >
                <span>{c.correspondant.nom || c.correspondant.adresse}</span>
                <span>{c.sujet || t('regie:boiteReception.sansSujet')}</span>
                <span>{c.extrait}</span>
                <span>
                  {t(`regie:boiteReception.canaux.${c.canal}`)} ·{' '}
                  {c.categorie
                    ? t(`regie:boiteReception.categories.${c.categorie}`)
                    : t('regie:boiteReception.sansCategorie')}
                </span>
                <span>{c.etiquettes.join(' · ')}</span>
                <span>
                  {equipe.data?.membres.find((m) => m.compteId === c.assigneeA)?.nom ??
                    t(
                      c.assigneeA
                        ? 'regie:boiteReception.assignee'
                        : 'regie:boiteReception.sansAssignation',
                    )}
                </span>
                {c.dernierEntrantLe ? (
                  <span>
                    {t('regie:boiteReception.attenteDepuis', {
                      date: formatDate(
                        new Date(c.dernierEntrantLe),
                        i18n.language === 'en' ? 'en' : 'fr',
                      ),
                    })}
                  </span>
                ) : null}
                {c.enAttenteJusquau ? (
                  <span>
                    {t('regie:boiteReception.attenteJusquau', {
                      date: formatDate(
                        new Date(c.enAttenteJusquau),
                        i18n.language === 'en' ? 'en' : 'fr',
                      ),
                    })}
                  </span>
                ) : null}
                {assistantActif && <ReperesAssistant c={c} />}
                {c.enRetard ? <span>{t('regie:boiteReception.enRetard')}</span> : null}
              </Link>
            ))}
            {liste.data ? (
              <div className={styles.gestes}>
                <Button
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => filtrer('page', String(page - 1))}
                >
                  {t('regie:boiteReception.precedent')}
                </Button>
                <span>
                  {page} / {Math.max(1, liste.data.pagination.pages)}
                </span>
                <Button
                  variant="secondary"
                  disabled={page >= liste.data.pagination.pages}
                  onClick={() => filtrer('page', String(page + 1))}
                >
                  {t('regie:boiteReception.suivant')}
                </Button>
              </div>
            ) : null}
          </div>
          {id ? (
            <div className={styles.conversation}>
              <Link className={styles.retour} to={`/${galerieSlug}/boite-reception?${params}`}>
                {t('regie:boiteReception.retour')}
              </Link>
              <ConversationVue key={id} id={id} />
            </div>
          ) : (
            <p>{t('regie:boiteReception.selectionner')}</p>
          )}
        </div>
      )}
    </section>
  );
}
